import type { CustomFunctionFileInput } from "src/api/generated/erp/customFunctions/model/customFunctionFileInput.ts";
import type {
	SubmittedCustomFunctionInputFileField,
} from "src/api/generated/erp/customFunctions/model/submittedCustomFunctionInputFileField.ts";
import type { FrontendUploadFileHandle } from "src/api/generated/common/frontendUploadFileHandle.ts";
import { toJsonObject, toRjsfSchema } from "src/components/common/forms/jsonSchema/jsonSchemaObjectInputUtils.ts";
import { uploadFiles } from "src/utils/fileUploading.ts";

export const MAX_CUSTOM_FUNCTION_INPUT_FILES = 50;
export const MAX_CUSTOM_FUNCTION_INPUT_FILE_BYTES = 100 * 1024 * 1024;
export const MAX_CUSTOM_FUNCTION_TOTAL_INPUT_BYTES = 300 * 1024 * 1024;

export type SelectedCustomFunctionInputFiles = Record<string, File[]>;

export type CustomFunctionInputFileValidationError =
	| "required"
	| "file_too_large"
	| "too_many_files"
	| "total_too_large";

export function mergeCustomFunctionFileInputErrors(
	validationErrors: Record<string, string>,
	selectionErrors: Record<string, string>,
): Record<string, string> {
	return { ...validationErrors, ...selectionErrors };
}

export function withCustomFunctionFileInputError(
	errors: Record<string, string>,
	inputField: string,
	error?: string,
): Record<string, string> {
	if (error != null) return { ...errors, [inputField]: error };
	const nextErrors = { ...errors };
	delete nextErrors[inputField];
	return nextErrors;
}

interface CustomFunctionInputFileLimits {
	maxFiles: number;
	maxFileBytes: number;
	maxTotalBytes: number;
}

const defaultLimits: CustomFunctionInputFileLimits = {
	maxFiles: MAX_CUSTOM_FUNCTION_INPUT_FILES,
	maxFileBytes: MAX_CUSTOM_FUNCTION_INPUT_FILE_BYTES,
	maxTotalBytes: MAX_CUSTOM_FUNCTION_TOTAL_INPUT_BYTES,
};

export function withoutCustomFunctionFileFields(value: unknown, fileInputs: CustomFunctionFileInput[]) {
	const result = { ...toJsonObject(value) };
	fileInputs.forEach(({ inputField }) => delete result[inputField]);
	return result;
}

export function withoutCustomFunctionFileSchemaFields(value: unknown, fileInputs: CustomFunctionFileInput[]) {
	const schema = toRjsfSchema(value);
	const fileFields = new Set(fileInputs.map(({ inputField }) => inputField));
	return {
		...schema,
		properties: Object.fromEntries(
			Object.entries(schema.properties ?? {}).filter(([field]) => !fileFields.has(field)),
		),
		required: Array.isArray(schema.required) ? schema.required.filter((field) => !fileFields.has(field)) : undefined,
	};
}

export function isAcceptedCustomFunctionInputFile(file: File, acceptedMimeTypes: string[]): boolean {
	if (acceptedMimeTypes.length === 0) return true;
	return acceptedMimeTypes.some((accepted) => {
		if (accepted.endsWith("/*")) return file.type.startsWith(accepted.slice(0, -1));
		return file.type === accepted;
	});
}

export function validateCustomFunctionInputFiles(
	definitions: CustomFunctionFileInput[],
	requiredFields: ReadonlySet<string>,
	selected: SelectedCustomFunctionInputFiles,
	limits: CustomFunctionInputFileLimits = defaultLimits,
): Record<string, CustomFunctionInputFileValidationError> {
	const errors: Record<string, CustomFunctionInputFileValidationError> = {};
	for (const definition of definitions) {
		const files = selected[definition.inputField] ?? [];
		if (requiredFields.has(definition.inputField) && files.length === 0) {
			errors[definition.inputField] = "required";
			continue;
		}
		const maximumSize = Math.min(definition.maxSizeBytes ?? limits.maxFileBytes, limits.maxFileBytes);
		if (files.some((file) => file.size > maximumSize)) {
			errors[definition.inputField] = "file_too_large";
		}
	}
	const errorField = definitions[0]?.inputField;
	if (errorField == null) return errors;
	const allFiles = Object.values(selected).flat();
	if (allFiles.length > limits.maxFiles) {
		errors[errorField] = "too_many_files";
	}
	if (allFiles.reduce((total, file) => total + file.size, 0) > limits.maxTotalBytes) {
		errors[errorField] = "total_too_large";
	}
	return errors;
}

export async function uploadCustomFunctionInputFiles(
	fileInputs: CustomFunctionFileInput[],
	selected: SelectedCustomFunctionInputFiles,
): Promise<SubmittedCustomFunctionInputFileField[]> {
	const selectedFields = fileInputs
		.map(({ inputField }) => ({ inputField, files: selected[inputField] ?? [] }))
		.filter(({ files }) => files.length > 0);
	const allFiles = selectedFields.flatMap(({ files }) => files);
	const handles = await uploadFiles(allFiles);
	return toSubmittedCustomFunctionInputFiles(selectedFields, handles);
}

export function toSubmittedCustomFunctionInputFiles(
	selectedFields: { inputField: string; files: File[] }[],
	handles: FrontendUploadFileHandle[],
): SubmittedCustomFunctionInputFileField[] {
	const expectedHandleCount = selectedFields.reduce((total, field) => total + field.files.length, 0);
	if (handles.length !== expectedHandleCount) {
		throw new Error("Custom Function input file upload did not return all file handles.");
	}
	let handleIndex = 0;
	return selectedFields.map(({ inputField, files }) => ({
		inputField,
		files: files.map((file) => ({
			fileHandle: handles[handleIndex++]!,
			fileName: file.name,
			mimeType: file.type || undefined,
		})),
	}));
}
